/**
 * The page an invitation link opens (CLAUDE.md 11.6).
 *
 * Sits outside both route guards, for the same reason `/s/:token` does: the
 * recipient may have no account at all, so `ProtectedRoute` would redirect them to a
 * sign-in they cannot complete, and `PublicOnlyRoute` would bounce a signed-in
 * colleague who was simply forwarded the link.
 *
 * It answers the recipient's question -- *what am I being asked to join, and as
 * what?* -- **before** asking them to authenticate. A valid invitation is itself the
 * authorisation to register even when open registration is off, so both routes out of
 * here are offered: sign in and accept, or create the account the invitation permits.
 *
 * A dead token gets a plain explanation and a way forward. "Not found" for a link
 * someone was told to click is a support ticket waiting to happen.
 */
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useAcceptInvitation, useInvitationPeek } from '@/api/workspaces'
import { useAuth } from '@/auth/AuthProvider'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'

export function InvitationPage() {
  const { t } = useTranslation()
  const { token } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user, loading, refreshWorkspaces, switchWorkspace } = useAuth()

  const { data: invitation, isLoading, isError } = useInvitationPeek(token)
  const accept = useAcceptInvitation()

  const onAccept = async () => {
    if (!token) return
    try {
      const workspace = await accept.mutateAsync(token)
      await refreshWorkspaces()
      switchWorkspace(workspace.id)
      toast({
        title: t('invitation.accepted', { workspace: workspace.name }),
        variant: 'success',
      })
      navigate('/projects', { replace: true })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    }
  }

  return (
    <AuthLayout title={t('invitation.title')}>
      {(isLoading || loading) && <Skeleton className="h-28 w-full" />}

      {!isLoading && isError && (
        <div className="space-y-4">
          <p role="alert" className="text-sm text-[var(--status-fail)]">
            {t('invitation.invalid')}
          </p>
          <p className="text-sm text-text-muted">{t('invitation.invalid_hint')}</p>
          <Button asChild className="w-full">
            <Link to="/login">{t('login.title', { ns: 'auth' })}</Link>
          </Button>
        </div>
      )}

      {!isLoading && !loading && invitation && (
        <div className="space-y-4">
          <p className="text-sm">
            {t('invitation.as_role', {
              workspace: invitation.workspace_name,
              role: t(`members.role.${invitation.role}`),
            })}
          </p>

          {user ? (
            <Button
              variant="primary"
              className="w-full"
              loading={accept.isPending}
              onClick={onAccept}
            >
              {t('invitation.join', { workspace: invitation.workspace_name })}
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-text-muted">
                {t('invitation.signed_out', { email: invitation.email })}
              </p>
              {/* One mechanism for "come back here afterwards": the login page reads
                  `next`, exactly as the route guard's `from` state does. */}
              <Button asChild variant="primary" className="w-full">
                <Link to={`/login?next=/invitations/${token}`}>{t('invitation.sign_in')}</Link>
              </Button>
              <Button asChild className="w-full">
                <Link to={`/register?invitation=${token}`}>{t('invitation.create_account')}</Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </AuthLayout>
  )
}
